import type { ToolCallApprovalPayload } from "@/components/assistant/findActiveWriteApproval";

function sameArgs(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.is(a[key], b[key]));
}

function samePayload(a: ToolCallApprovalPayload, b: ToolCallApprovalPayload): boolean {
  return (
    a.runId === b.runId &&
    a.toolCallId === b.toolCallId &&
    a.toolName === b.toolName &&
    sameArgs(a.args, b.args)
  );
}

function sameApprovalMaps(
  a: Record<string, ToolCallApprovalPayload>,
  b: Record<string, ToolCallApprovalPayload>,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => {
    const left = a[key];
    const right = b[key];
    return Boolean(left && right && samePayload(left, right));
  });
}

export function mergePendingWriteApprovalMaps(
  prev: Record<string, ToolCallApprovalPayload>,
  nextFromServer: Record<string, ToolCallApprovalPayload>,
  resolvedToolCallIds?: ReadonlySet<string>
): Record<string, ToolCallApprovalPayload> {
  const merged = { ...prev };
  for (const id of resolvedToolCallIds ?? []) {
    delete merged[id];
  }
  for (const [id, payload] of Object.entries(nextFromServer)) {
    if (resolvedToolCallIds?.has(id)) continue;
    merged[id] = payload;
  }
  const removedResolved = [...(resolvedToolCallIds ?? [])].some((id) => id in prev);
  if (Object.keys(nextFromServer).length === 0 && !removedResolved) {
    return prev;
  }
  if (sameApprovalMaps(prev, merged)) {
    return prev;
  }
  return merged;
}
